/**
 * Operative legal copy for Iron Resolve Combat Fitness.
 *
 * Headings use `## ` / `### ` markers consumed by LegalDocument.
 */

const BUSINESS = 'Iron Resolve Combat Fitness';

export const LEGAL_DOCUMENT_VERSION = '2026-08-15';

export const privacyPolicy = `
## Who we are

${BUSINESS} ("we", "us") provides personal training, boxing coaching and general nutrition education in Australia. This Privacy Policy explains how we collect, use, store and disclose personal information, including health information, when you use our website, client portal or coaching services.

## The information we collect

We collect information you give us directly: your name, contact details, account credentials, booking history, goals, training logs, nutrition logs, messages, documents you upload, and answers to health and lifestyle screening questions. We also collect session attendance, progress measurements and, where you consent, progress photographs.

If you contact us, we keep the contents of that enquiry so we can reply.

Technical data such as browser type, device class and pages visited may be recorded to keep the service working. We do not sell personal information.

## Health and sensitive information

Health screening answers, injury notes, medications you choose to disclose, progress photographs and similar records are treated as private client data. They are never shown on a public page. Access is limited to you and the coaches assigned to you, plus administrators who need access to operate the service.

We collect this information so we can coach you safely, individualise programming and keep an accurate history of your training. We do not use it to diagnose or treat medical conditions.

## How we use information

We use personal information to:

create and manage your account
deliver coaching, bookings, programs and nutrition guidance
review health screening before training begins
record consents and keep an audit trail of access to private files
send transactional messages such as booking confirmations, reminders and password resets
improve the reliability and accessibility of the platform
meet legal obligations, including Australian Consumer Law and record-keeping duties

## Public results and photographs

Private progress photographs stay private to you and your coach. They are never used in marketing unless you give a separate, explicit public-result consent. You can withdraw that consent at any time. Withdrawal unpublishes the public content and does not delete the private record behind it.

## Storage, access and security

Private files are held in private storage and served only through short-lived, ownership-checked links. They are not addressable by a guessable URL. Authentication uses an HTTP-only session cookie. The browser never holds a token that a script could read.

Access to client records is role-based and checked on every request. Access to private files is written to an audit log.

## Analytics

Where analytics are enabled, they receive only an allow-listed set of non-identifying properties. Health answers, messages, measurements, names and contact details are excluded.

## Disclosure

We do not sell your information. We may disclose it:

to coaches and administrators who need it to deliver the service
to infrastructure providers who host the platform, under contract and only for that purpose
where the law requires it, or to protect someone's safety
with your consent

## Retention

We keep account, booking, consent and coaching records for as long as your account is active and for a reasonable period afterwards so we can resolve disputes, meet legal duties and reconstruct a training history if you return. You can ask us to delete personal information we no longer need to keep.

## Your rights

You may request access to the personal information we hold about you, and ask us to correct it. You may withdraw optional consents at any time. To make a request, use the contact details on our Contact page.

If you are not satisfied with how we handle a privacy matter, you may complain to the Office of the Australian Information Commissioner.

## Children

Our coaching services are not directed at children under 16. A parent or guardian must make the account and give consent before anyone under 18 trains with us.

## Changes

We may update this policy. The document version on this page is the current version. Material changes will be notified through the client portal or by email where we have an address for you.

## Contact

Questions about this policy should be sent using the contact details on our Contact page.
`.trim();

export const termsAndConditions = `
## Agreement

These terms govern use of the ${BUSINESS} website, client portal and coaching services, including personal training, boxing coaching, online and hybrid coaching, and general nutrition education. By creating an account, booking a consultation or starting a program, you agree to these terms.

## Who we are

${BUSINESS} is an Australian coaching business. "You" means the client or visitor using the service.

## Services

We provide coaching, not medical care. Programs are individualised from the information you give us. Results vary. We do not guarantee a specific outcome, body composition change, strength number or fight result.

Nutrition guidance is general coaching education within a fitness professional's scope. It is not medical nutrition therapy and does not replace a doctor or accredited practising dietitian.

Boxing coaching does not require sparring. Sparring, if ever offered, is optional and only by separate agreement.

## Accounts

You must give accurate information and keep your login details confidential. You are responsible for activity on your account. Tell us immediately if you think it has been misused.

We may suspend or close an account that is used to abuse staff or other clients, to circumvent booking or payment rules, or to put anyone at risk.

## Bookings and payment

Session types, prices, credits and availability are as shown at the time you book or as agreed in writing. A listed price of "Enquire" is not an offer until we confirm it.

Packages, session credits and expiry are described in the Cancellation Policy. The booking system enforces the notice periods stated there.

## Your responsibilities

Complete health screening honestly before your first training session. Tell your coach immediately if your health, medication, injury status or ability to train changes. Follow the technique and load guidance given. Stop any exercise that causes pain and seek medical advice if you feel unwell.

You confirm you are medically suitable to take part in physical training, or that you have obtained clearance from a doctor where that is appropriate.

## Intellectual property

Programs, session plans, nutrition targets, written coaching content and the website design belong to ${BUSINESS} or its licensors. You may use them for your own training while you are a client. You may not copy, share or resell them.

## Acceptable use of the portal

Do not upload unlawful, abusive or someone else's confidential material. Do not attempt to access another client's records. Private messages and documents are for coaching, not for publication.

## Liability

Physical training and boxing carry inherent risk of injury. To the extent permitted by Australian Consumer Law, we are not liable for loss that we could not reasonably have avoided, or for loss caused by your failure to disclose health information, to follow coaching instruction, or to stop when in pain.

Nothing in these terms excludes rights you have under the Australian Consumer Law that cannot be excluded.

## Changes and termination

We may update these terms. The document version on this page is current. Either party may end the coaching relationship in accordance with the Cancellation Policy. After termination, these terms continue to apply to past sessions, credits and records.

## Governing law

These terms are governed by the laws of New South Wales, Australia.

## Contact

Questions about these terms should be sent using the contact details on our Contact page.
`.trim();

export const trainingDisclaimer = `
## Acknowledgement of risk

Physical training, including resistance training, conditioning, mobility work and boxing, involves inherent risk of injury, illness and, in rare cases, serious harm. By taking part you acknowledge that risk and agree to train within your current capability.

This disclaimer applies to in-person, online and hybrid coaching delivered by ${BUSINESS}.

## Not medical advice

Coaching is not a medical assessment, diagnosis or treatment. A health screening is a routing step so your coach can program safely. Flagging an answer for review is not an interpretation of your health and is not a diagnosis.

## Your duty to disclose

You must complete screening honestly before your first training session. You must tell your coach immediately if your health, medication, pregnancy status, injury or ability to train changes. A program written on outdated information is not a safe program.

Where screening or your history suggests it, we may require written medical clearance from your doctor before training begins or continues.

## During sessions

Follow the technique, load and rest guidance given. Ask if you are unsure. Stop any exercise that causes pain, dizziness, chest pain, unusual shortness of breath or distress. Seek medical advice if you feel unwell during or after a session.

Boxing work is coached with an emphasis on stance, movement, defence and pad or bag work. Sparring is not required.

## Online and unsupervised work

Where you complete programmed work away from your coach, you are responsible for a safe environment, suitable equipment and stopping when something is not right. Remote coaching cannot see everything a coach in the room can see.

## Photographs and measurements

Progress photographs, measurements and body-composition records are optional, taken only with your consent, and remain private unless you give separate public-result consent.

## Standing statement

Physical training carries inherent risk. Follow the technique guidance provided, work within your current capability, and stop any exercise that causes pain. This program is based on the information you have given your coach - tell your coach immediately if your health, medication or injury status changes.
`.trim();

export const nutritionDisclaimer = `
## Scope

Nutrition guidance from ${BUSINESS} is general coaching education provided by a fitness professional. It is individualised to your stated goals, preferences, training and the information you give us.

It is not medical nutrition therapy. It does not diagnose, treat or manage any medical condition, eating disorder, allergy or clinical nutrition need. It does not replace advice from your doctor, an accredited practising dietitian or another qualified health professional.

## What guidance may include

Energy and protein targets, meal structure, portion education, hydration habits and practical food choices that support your training. Targets are adjusted from your recorded data and feedback.

## What we will not do

We will not prescribe therapeutic diets for disease, provide clinical meal plans for diagnosed conditions, or present ourselves as dietitians unless that qualification is separately verified and stated.

Anything outside this scope is referred to your doctor or to an accredited practising dietitian.

## Your responsibility

Tell your coach about allergies, intolerances, medications, supplements and any condition that affects what you can eat. Stop and seek medical advice if you feel unwell. Do not follow a target that conflicts with advice from your treating practitioner.

## Standing statement

Nutrition targets and guidance are provided as general coaching education by a fitness professional and are individualised to your stated goals, preferences and training. They are not medical nutrition therapy, do not diagnose or treat any condition, and do not replace advice from your doctor or an accredited practising dietitian. Stop and seek medical advice if you feel unwell.
`.trim();

export const cancellationPolicy = `
## Purpose

This policy sets how bookings, cancellations, reschedules, no-shows, session credits and refunds work at ${BUSINESS}. The booking system enforces these rules on every session.

## Booking notice

You cannot request a session starting sooner than 12 hours from the time of booking. You can book up to 60 days ahead.

Consultations are a conversation about your goals and the right way to reach them. They do not require completed onboarding. Training sessions do.

## Free cancellation

Cancel at least 24 hours before the session starts and the session credit is returned in full.

## Late cancellation

If you cancel inside 24 hours, the session credit is forfeited unless your coach records an exception, for example for illness or an emergency. Ask as soon as you know you cannot attend.

## No-show

A missed session without notice forfeits the credit.

## Rescheduling

You may request a reschedule instead of cancelling. A reschedule requested at least 24 hours ahead is treated as a free change. Inside that window it is treated as a late cancellation unless your coach agrees otherwise.

## Credits and packages

Session credits are used when a session is delivered, or when a late cancellation or no-show forfeits them. Credits expire 180 days after the package is activated, unless a different period is agreed in writing.

Every credit movement - use, return, forfeit or adjustment - is written to a ledger you can read in the client portal.

## Refunds

Unused credits still inside their expiry window may be refunded at our discretion where Australian Consumer Law requires it, or where we agree in writing. Forfeited credits for late cancellation or no-show are not refunded except where we record an exception.

If we cancel a session, the credit is returned in full and we will offer a new time.

Complimentary consultations are not purchased credits and have no cash value.

## How to cancel or reschedule

Use the client portal or contact us using the details on the Contact page. The time of the request is the time we receive it.

## Changes

We may update this policy. The document version on this page is current. Existing confirmed sessions keep the notice window that applied when they were booked, unless a change benefits you.
`.trim();

export const refundPolicy = `
Unused session credits still inside their expiry window may be refunded where Australian Consumer Law requires it, or where ${BUSINESS} agrees in writing. Credits forfeited for late cancellation or no-show are not refunded except where a coach records an exception. If we cancel a session, the credit is returned in full. Complimentary consultations have no cash value. See the Cancellation Policy for notice windows and the credit ledger.
`.trim();

export const accessibilityStatement = `
## Commitment

${BUSINESS} aims to make this website and client portal usable by everyone. We target WCAG 2.2 Level AA. All content and actions are available without WebGL, with reduced motion enabled, and by keyboard alone. Every interactive 3D element has an equivalent standard control.

## Keyboard and focus

Every interactive control is reachable and operable with a keyboard alone. Focus is always visible. A skip link is the first focusable element on every page. Dialogs trap focus, close on Escape, and return focus to the control that opened them. Route changes move focus and announce the new page title. Error summaries receive focus on failed submission.

## Motion

The operating system reduce-motion preference is respected, including in 3D scenes. You can also turn animation off for this browser tab on this page. With motion reduced, scenes render as static imagery. Nothing auto-plays audio or flashes.

## 3D scenes

3D scenes are decorative. Every headline, list, form and action they depict exists as ordinary HTML. Where WebGL is unavailable, blocked or too slow, a poster image is shown instead. If the 3D layer fails it is replaced with that poster rather than breaking the page.

## Colour and layout

Text and interface colours target WCAG 2.2 AA contrast. Colour is never the only carrier of meaning. Layouts reflow at narrow widths and support 200% zoom. Primary touch targets meet a 44-pixel minimum.

## Known limitations

This statement describes measures that are implemented and tested during development. The platform has not yet been through an independent accessibility audit.

## Reporting a barrier

If you encounter a barrier, contact us using the details on our Contact page and tell us what you were trying to do. Accessibility reports are treated as defects.
`.trim();
